import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';

import { buildCaptainComparison } from './utils/captain-comparison';
import { mapRaceBarPositions } from './utils/race-bar';
import {
  deriveRaceMetrics,
  projectGap,
  resolveRaceConfig
} from './utils/race-state';
import {
  calculateReadinessScore,
  resolveOddsBandIndex,
  resolveScoringConfig
} from './utils/readiness-score';
import { buildReplacementCards } from './utils/transfer-replacements';
import { mapSquadToTeam } from '../shared/map-squad';
import { fetchTransferReplacements } from '@lib/api/transfer-replacements';
import { fetchPlayers } from '@api-service/players';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { fetchEntryLeagues } from '@api-service/fpl-entries';
import { reportFunnelCompletion } from '@lib/api/funnel-email-capture';
import {
  buildFunnelCompletedSnapshot,
  getFunnelDimensions
} from '@lib/funnels/funnel-dimensions';
import { fireLeadEvent } from '@lib/analytics/fireLead';
import { importTeamFromScreenshot as importScreenshot } from '@lib/api/import-team';
import {
  assessScreenshotSquad,
  FULL_SQUAD_SIZE
} from '@lib/quiz/screenshot-squad';
import {
  fetchTeamRatingByFplId,
  fetchTeamRatingByPlayerIds
} from '@lib/api/team-rating';
import type {
  ReadinessStakeKey,
  SanityReadinessRaceConfig,
  SanityReadinessScoringConfig,
  SanityReadinessVerdictState
} from '@lib/sanity/types';
import type { QuizPlayer } from 'types/quiz-player';
import type {
  MiniLeagueAnswer,
  MiniLeagueSummary,
  RaceVerdictResult,
  ReadinessCaptainOutcome,
  ReadinessContextValue,
  ReadinessState,
  ReadinessTeamMember,
  ReadinessTransferChoice,
  ReadinessTransferOutcome,
  ReadinessVerdictResult,
  ScreenshotImportStatus
} from 'types/readiness';
import { INITIAL_READINESS_STATE } from 'types/readiness';

const DEFAULT_SCREENSHOT_READ_ERROR =
  "We couldn't read your team from that screenshot. Please try again or enter your FPL ID instead.";

const deriveCaptainOutcome = (
  state: ReadinessState
): ReadinessCaptainOutcome => {
  if (state.isCaptainSkipped || !state.captainComparison) return 'skipped';
  return state.captainComparison.isUserMatchingAi ? 'match' : 'mismatch';
};

const deriveTransferOutcome = (
  state: ReadinessState
): ReadinessTransferOutcome => {
  if (!state.transferChoice) return 'undecided';
  return state.transferChoice.type === 'roll' ? 'roll' : 'selected';
};

const ReadinessContext = createContext<ReadinessContextValue | null>(null);

export const useReadiness = (): ReadinessContextValue => {
  const context = useContext(ReadinessContext);

  if (!context) {
    throw new Error('useReadiness must be used within a ReadinessProvider');
  }

  return context;
};

type ReadinessProviderProps = {
  children: ReactNode;
  templateKey: string;
};

const ReadinessProvider: FC<ReadinessProviderProps> = ({
  children,
  templateKey
}) => {
  const [state, setState] = useState<ReadinessState>(INITIAL_READINESS_STATE);

  const { context } = useLeagueContext();
  const nextGameweek = context?.nextGameweek?.number ?? null;

  const isPreGw1 = templateKey === 'readiness-pre-gw1';
  const isPostGw1 = templateKey === 'readiness-post-gw1';

  /**
   * Rates a screenshot-sourced squad and stores it, from either the read
   * itself or the review pitch that corrected it.
   *
   * Captain and vice follow slot order, matching what the import has always
   * sent: the visitor picks their own captain later in the funnel.
   */
  const rateScreenshotSquad = useCallback(
    async (playerIds: string[], readErrorText?: string): Promise<boolean> => {
      setState((previous) => ({
        ...previous,
        isImporting: true,
        importError: null
      }));

      try {
        const ratingResult = await fetchTeamRatingByPlayerIds(
          playerIds.map((id, index) => ({
            id,
            isCaptain: index === 0,
            isViceCaptain: index === 1
          })),
          // The screenshot chose these players, and a correction on the review
          // pitch is a correction to that choice, not a fresh assembly.
          'SCREENSHOT'
        );
        const team = mapSquadToTeam(ratingResult.squad);

        setState((previous) => ({
          ...previous,
          team,
          teamSource: 'screenshot',
          entryMethod: 'SCREENSHOT',
          squadRating: ratingResult.rating,
          aiCaptainId: ratingResult.aiCaptainId,
          aiSellPlayerId: ratingResult.aiSellPlayerId,
          isReviewingScreenshot: false,
          isImporting: false
        }));
        return true;
      } catch (error) {
        setState((previous) => ({
          ...previous,
          isImporting: false,
          // The rating names what is wrong with a squad it refuses, which is
          // more use on the review pitch than a generic read failure.
          importError:
            error instanceof Error && error.message
              ? error.message
              : readErrorText ?? DEFAULT_SCREENSHOT_READ_ERROR
        }));
        return false;
      }
    },
    []
  );

  const confirmTeamFromReview = useCallback(
    (playerIds: string[]) => rateScreenshotSquad(playerIds),
    [rateScreenshotSquad]
  );

  const setIsReviewingScreenshot = useCallback((isReviewing: boolean) => {
    setState((previous) => ({
      ...previous,
      isReviewingScreenshot: isReviewing,
      ...(isReviewing ? {} : { importError: null })
    }));
  }, []);

  const importTeamFromScreenshot = useCallback(
    async (
      file: File,
      options?: { readErrorText?: string }
    ): Promise<ScreenshotImportStatus> => {
      setState((previous) => ({
        ...previous,
        isImporting: true,
        importError: null
      }));

      try {
        const reader = new FileReader();
        const base64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => {
            const payload =
              typeof reader.result === 'string'
                ? reader.result.split(',')[1]
                : undefined;

            if (payload) {
              resolve(payload);
            } else {
              reject(new Error('Could not read the selected file.'));
            }
          };
          reader.onerror = () =>
            reject(
              reader.error ?? new Error('Could not read the selected file.')
            );
          reader.readAsDataURL(file);
        });

        const screenshotResult = await importScreenshot(base64);
        const players = screenshotResult.team.players;

        // A read the rules refuse is not a failed read: the review pitch can
        // place what fitted and let the visitor finish the squad, where the old
        // partial-read error told them their screenshot was unusable and left
        // them nothing to do (FFH-2738).
        if (!assessScreenshotSquad(players).isComplete) {
          setState((previous) => ({
            ...previous,
            isImporting: false,
            screenshotMatches: players,
            isReviewingScreenshot: true
          }));
          return 'needs-review';
        }

        setState((previous) => ({ ...previous, screenshotMatches: players }));

        const isRated = await rateScreenshotSquad(
          players.map((match) => match.player.id),
          options?.readErrorText
        );
        return isRated ? 'imported' : 'error';
      } catch {
        setState((previous) => ({
          ...previous,
          isImporting: false,
          importError: options?.readErrorText ?? DEFAULT_SCREENSHOT_READ_ERROR
        }));
        return 'error';
      }
    },
    [rateScreenshotSquad]
  );

  const importTeamByFplId = useCallback(async (fplId: string) => {
    setState((previous) => ({
      ...previous,
      isImporting: true,
      importError: null,
      fplId
    }));

    try {
      const numericId = parseInt(fplId, 10);
      if (Number.isNaN(numericId)) {
        setState((previous) => ({
          ...previous,
          isImporting: false,
          importError: 'Please enter a valid FPL ID (numbers only).'
        }));
        return;
      }

      const result = await fetchTeamRatingByFplId(numericId);
      const team = mapSquadToTeam(result.squad);

      setState((previous) => ({
        ...previous,
        team,
        teamSource: 'fplId',
        // The backend fetches this squad from FPL, so there is no client-side
        // entry method to report.
        entryMethod: null,
        squadRating: result.rating,
        aiCaptainId: result.aiCaptainId,
        aiSellPlayerId: result.aiSellPlayerId,
        isImporting: false
      }));
    } catch {
      setState((previous) => ({
        ...previous,
        isImporting: false,
        importError:
          'Could not find that FPL team. Please check your ID and try again.'
      }));
    }
  }, []);

  const setTeamManually = useCallback((team: ReadinessTeamMember[]) => {
    setState((previous) => ({
      ...previous,
      team,
      teamSource: 'manual',
      // PICKER even when a screenshot was imported earlier in the session: the
      // picker starts empty, so a hand-built squad is a fresh assembly that
      // shares nothing with the abandoned import, and attributing it to the
      // import would blame OCR for players the member chose themselves. See the
      // TeamEntryMethod docs for the seeded-picker case.
      entryMethod: 'PICKER',
      importError: null
    }));
  }, []);

  const confirmTeamFromSearch = useCallback(
    async (entry: {
      fplEntryId: number;
      teamName: string;
      managerName: string;
    }) => {
      setState((previous) => ({
        ...previous,
        isImporting: true,
        importError: null,
        fplId: String(entry.fplEntryId),
        teamMeta: {
          fplId: entry.fplEntryId,
          teamName: entry.teamName,
          managerName: entry.managerName,
          overallRank: null,
          gameweekPoints: null
        }
      }));

      try {
        const result = await fetchTeamRatingByFplId(entry.fplEntryId);
        const team = mapSquadToTeam(result.squad);
        const overallRank = result.performance?.rank?.overallRank ?? null;
        const gameweekPoints = result.performance?.squadGameweekPoints ?? null;

        setState((previous) => ({
          ...previous,
          team,
          teamSource: 'fplId',
          // The backend fetches this squad from FPL, so there is no
          // client-side entry method to report.
          entryMethod: null,
          squadRating: result.rating,
          aiCaptainId: result.aiCaptainId,
          aiSellPlayerId: result.aiSellPlayerId,
          teamMeta: previous.teamMeta
            ? { ...previous.teamMeta, overallRank, gameweekPoints }
            : previous.teamMeta,
          isImporting: false
        }));
        return true;
      } catch {
        setState((previous) => ({
          ...previous,
          isImporting: false,
          importError:
            'Could not load that FPL team. Please search again and pick another result.'
        }));
        return false;
      }
    },
    []
  );

  const setTeamFromTemplate = useCallback(async (optaPlayerIds: string[]) => {
    setState((previous) => ({
      ...previous,
      isImporting: true,
      importError: null
    }));

    try {
      // Predictions are excluded: the template only needs the player ids to
      // resolve the squad, and the server now picks the starting XI, so no
      // prediction data is read client-side to order the lineup.
      const response = await fetchPlayers({
        optaIds: optaPlayerIds.join(','),
        limit: FULL_SQUAD_SIZE,
        exclude: 'predictions'
      });

      // The API can return fewer players than requested (unknown OPTA
      // IDs, filters, outages) — never rate an incomplete squad.
      if (response.data.length !== FULL_SQUAD_SIZE) {
        setState((previous) => ({
          ...previous,
          isImporting: false,
          importError:
            'Could not build that squad right now. Please try again or import your own team.'
        }));
        return false;
      }

      // Submit the flat 15 with no lineup or captain: the server picks the
      // highest-predicted valid starting XI and captain, and returns them via
      // each squad member's slot / isCaptain fields.
      //
      // A template squad is assembled in-app from curated player ids, never
      // read off an image, so it belongs on the picker side of the split.
      const ratingResult = await fetchTeamRatingByPlayerIds(
        response.data.map((player) => ({ id: player.id })),
        'PICKER'
      );
      const team = mapSquadToTeam(ratingResult.squad);

      setState((previous) => ({
        ...previous,
        team,
        teamSource: 'manual',
        entryMethod: 'PICKER',
        squadRating: ratingResult.rating,
        aiCaptainId: ratingResult.aiCaptainId,
        aiSellPlayerId: ratingResult.aiSellPlayerId,
        teamMeta: null,
        isImporting: false
      }));
      return true;
    } catch {
      setState((previous) => ({
        ...previous,
        isImporting: false,
        importError:
          'Could not build that squad right now. Please try again or import your own team.'
      }));
      return false;
    }
  }, []);

  const setLeagueName = useCallback((name: string) => {
    setState((previous) => ({
      ...previous,
      leagueName: name.trim() || null,
      isLeagueNameSkipped: false
    }));
  }, []);

  const skipLeagueName = useCallback(() => {
    setState((previous) => ({
      ...previous,
      leagueName: null,
      isLeagueNameSkipped: true
    }));
  }, []);

  const skipCaptain = useCallback(() => {
    setState((previous) => ({
      ...previous,
      selectedCaptain: null,
      captainComparison: null,
      isCaptainSkipped: true
    }));
  }, []);

  const setTransferChoice = useCallback(
    (choice: ReadinessTransferChoice | null) => {
      setState((previous) => ({
        ...previous,
        transferChoice: choice,
        selectedReplacementIndex:
          choice?.type === 'replacement' ? choice.index : null
      }));
    },
    []
  );

  const setStakes = useCallback((stakes: ReadinessStakeKey) => {
    setState((previous) => ({ ...previous, stakes }));
  }, []);

  const setScoringConfig = useCallback(
    (config: Partial<SanityReadinessScoringConfig> | null) => {
      setState((previous) => ({ ...previous, scoringConfig: config }));
    },
    []
  );

  const calculateReadinessVerdict = useCallback(
    (oddsBands: { minGap: number; maxGap: number }[]) => {
      setState((previous) => {
        const captainOutcome = deriveCaptainOutcome(previous);
        const transferOutcome = deriveTransferOutcome(previous);
        const config = resolveScoringConfig(previous.scoringConfig);
        const { score, winnersTarget, gap } = calculateReadinessScore(
          {
            squadRating: previous.squadRating,
            captainOutcome,
            transferOutcome,
            stakes: previous.stakes
          },
          config
        );

        const verdictResult: ReadinessVerdictResult = {
          score,
          winnersTarget,
          gap,
          oddsBandIndex: resolveOddsBandIndex(gap, oddsBands),
          captainOutcome,
          transferOutcome
        };

        return { ...previous, readinessVerdict: verdictResult };
      });

      // Side effects run unconditionally after setState (the updater always
      // computes a verdict) — never gated on a flag mutated inside the
      // updater, which React may not run synchronously. Mirrors
      // calculateRaceVerdict.
      //
      // The backend fires the CleverTap completion event on this same
      // payload. Base props only — the sanity_ml_* result properties are
      // deprioritised for this season (spec §4.2).
      const resultSnapshot = buildFunnelCompletedSnapshot(
        getFunnelDimensions(templateKey)
      );
      reportFunnelCompletion({
        funnelSlug: templateKey,
        resultSnapshot,
        email: state.capturedEmail ?? undefined
      });
      // FFH-990: mini-league-readiness Lead on both rails (Pattern A).
      // funnel_result (ready|not_ready) deferred pending the product threshold.
      fireLeadEvent('mini_league_readiness');
    },
    [templateKey, state.capturedEmail]
  );

  // Post-GW1 race actions (FFH-1069)

  const fetchMiniLeagues = useCallback(async () => {
    const fplId = state.teamMeta?.fplId ?? null;

    if (!fplId) {
      setState((previous) => ({
        ...previous,
        isLoadingLeagues: false,
        leaguesError: 'No FPL team ID available'
      }));
      return;
    }

    setState((previous) => ({
      ...previous,
      isLoadingLeagues: true,
      leaguesError: null
    }));

    try {
      const leagues = await fetchEntryLeagues(fplId);
      setState((previous) => ({
        ...previous,
        miniLeagues: leagues,
        isLoadingLeagues: false
      }));
    } catch (error) {
      setState((previous) => ({
        ...previous,
        isLoadingLeagues: false,
        leaguesError:
          error instanceof Error
            ? error.message
            : 'Failed to load your mini-leagues'
      }));
    }
  }, [state.teamMeta]);

  const selectLeague = useCallback((league: MiniLeagueSummary) => {
    // Also sets leagueName so the verdict templates and adaptive CTA can
    // reference the real league exactly like the pre-GW1 named league.
    setState((previous) => ({
      ...previous,
      selectedLeague: league,
      leagueName: league.name,
      isLeagueNameSkipped: false
    }));
  }, []);

  const setRaceConfig = useCallback(
    (config: Partial<SanityReadinessRaceConfig> | null) => {
      setState((previous) => ({ ...previous, raceConfig: config }));
    },
    []
  );

  const calculateRaceVerdict = useCallback(() => {
    setState((previous) => {
      const config = resolveRaceConfig(previous.raceConfig);

      // The manual/OCR path has no real standings; never fabricate one.
      const raceVerdict: RaceVerdictResult = previous.selectedLeague
        ? (() => {
            const { state: raceState, gap } = deriveRaceMetrics(
              previous.selectedLeague,
              config
            );
            return {
              state: raceState,
              gap,
              projectedGap: projectGap(raceState, gap, config),
              userRank: previous.selectedLeague.userRank,
              leagueSize: previous.selectedLeague.leagueSize,
              leagueName: previous.selectedLeague.name
            };
          })()
        : {
            state: 'lateStart',
            gap: 0,
            projectedGap: null,
            userRank: null,
            leagueSize: null,
            leagueName: previous.leagueName
          };

      return { ...previous, raceVerdict };
    });

    // The backend fires the CleverTap completion event on this same payload.
    // Base props only — the sanity_ml_* result properties are deprioritised
    // for this season (spec §4.2).
    const resultSnapshot = buildFunnelCompletedSnapshot(
      getFunnelDimensions(templateKey)
    );
    reportFunnelCompletion({
      funnelSlug: templateKey,
      resultSnapshot,
      email: state.capturedEmail ?? undefined
    });
    // FFH-990: mini-league-readiness Lead on both rails (Pattern A).
    fireLeadEvent('mini_league_readiness');
  }, [templateKey, state.capturedEmail]);

  const selectCaptain = useCallback(
    async (player: QuizPlayer) => {
      // Reflect the pick immediately; the comparison fills in once the server
      // returns the captain delta.
      setState((previous) => ({
        ...previous,
        selectedCaptain: player,
        isCaptainSkipped: false
      }));

      const squadPlayerIds = state.team.reduce<string[]>((ids, member) => {
        if (member.player) ids.push(member.player.id);
        return ids;
      }, []);

      // A vice-captain is required by the rating API but irrelevant to the
      // captain delta, so any other squad member serves.
      const viceCaptainId = squadPlayerIds.find((id) => id !== player.id);

      try {
        // A captain re-rate resubmits the same squad, so it carries however
        // that squad was assembled. An FPL-sourced squad has no entry method,
        // and omitting it is truer than guessing one.
        const result = await fetchTeamRatingByPlayerIds(
          squadPlayerIds.map((id) => ({
            id,
            ...(id === player.id && { isCaptain: true }),
            ...(id === viceCaptainId && { isViceCaptain: true })
          })),
          state.entryMethod ?? undefined
        );

        setState((previous) => {
          // Ignore a response that arrives after the user has picked a
          // different captain, so a stale re-rate can't overwrite the newer one.
          if (previous.selectedCaptain?.id !== player.id) return previous;

          return {
            ...previous,
            aiCaptainId: result.aiCaptainId,
            aiSellPlayerId: result.aiSellPlayerId,
            captainComparison: buildCaptainComparison(
              previous.team,
              player,
              result.aiCaptainId,
              result.captainPointsDelta
            )
          };
        });
      } catch {
        // On failure, still record the pick using the AI captain already in
        // state and a neutral delta, so the flow can continue. Same staleness
        // guard as the success path.
        setState((previous) => {
          if (previous.selectedCaptain?.id !== player.id) return previous;

          return {
            ...previous,
            captainComparison: buildCaptainComparison(
              previous.team,
              player,
              previous.aiCaptainId,
              null
            )
          };
        });
      }
    },
    [state.team, state.entryMethod]
  );

  const selectTransferOut = useCallback(
    async (player: QuizPlayer) => {
      const squadPlayerIds = state.team.reduce<string[]>((ids, member) => {
        if (member.player) ids.push(member.player.id);
        return ids;
      }, []);

      setState((previous) => ({
        ...previous,
        selectedTransferOut: player,
        transferReplacements: null,
        isLoadingReplacements: true,
        replacementError: null
      }));

      try {
        const result = await fetchTransferReplacements(
          player.id,
          squadPlayerIds
        );

        setState((previous) => ({
          ...previous,
          transferReplacements: buildReplacementCards(result),
          isLoadingReplacements: false
        }));
      } catch {
        setState((previous) => ({
          ...previous,
          isLoadingReplacements: false,
          replacementError:
            'Could not load replacement suggestions. Please try again.'
        }));
      }
    },
    [state.team]
  );

  const selectReplacement = useCallback((index: number) => {
    setState((previous) => ({
      ...previous,
      selectedReplacementIndex: index
    }));
  }, []);

  const setMiniLeagueAnswer = useCallback((answer: MiniLeagueAnswer) => {
    setState((previous) => ({
      ...previous,
      miniLeagueAnswer: answer
    }));
  }, []);

  const setVerdictStates = useCallback(
    (states: SanityReadinessVerdictState[]) => {
      setState((previous) => ({ ...previous, verdictStates: states }));
    },
    []
  );

  const setCapturedEmail = useCallback((email: string) => {
    setState((previous) => ({ ...previous, capturedEmail: email }));
  }, []);

  const resetImportError = useCallback(() => {
    setState((previous) => ({ ...previous, importError: null }));
  }, []);

  const resetForRetry = useCallback(() => {
    setState(INITIAL_READINESS_STATE);
  }, []);

  const actions = useMemo(
    () => ({
      importTeamFromScreenshot,
      confirmTeamFromReview,
      setIsReviewingScreenshot,
      importTeamByFplId,
      setTeamManually,
      setCapturedEmail,
      selectCaptain,
      selectTransferOut,
      selectReplacement,
      setMiniLeagueAnswer,
      setVerdictStates,
      resetForRetry,
      resetImportError,
      confirmTeamFromSearch,
      setTeamFromTemplate,
      setLeagueName,
      skipLeagueName,
      skipCaptain,
      setTransferChoice,
      setStakes,
      setScoringConfig,
      calculateReadinessVerdict,
      fetchMiniLeagues,
      selectLeague,
      setRaceConfig,
      calculateRaceVerdict
    }),
    [
      importTeamFromScreenshot,
      confirmTeamFromReview,
      setIsReviewingScreenshot,
      importTeamByFplId,
      setTeamManually,
      setCapturedEmail,
      selectCaptain,
      selectTransferOut,
      selectReplacement,
      setMiniLeagueAnswer,
      setVerdictStates,
      resetForRetry,
      resetImportError,
      confirmTeamFromSearch,
      setTeamFromTemplate,
      setLeagueName,
      skipLeagueName,
      skipCaptain,
      setTransferChoice,
      setStakes,
      setScoringConfig,
      calculateReadinessVerdict,
      fetchMiniLeagues,
      selectLeague,
      setRaceConfig,
      calculateRaceVerdict
    ]
  );

  // Live readiness for the sticky jeopardy bar: recomputed as the user
  // progresses, before the verdict is locked in at the processing step.
  const liveReadiness = useMemo(() => {
    if (state.team.length === 0) return null;

    const { score, winnersTarget } = calculateReadinessScore(
      {
        squadRating: state.squadRating,
        captainOutcome: deriveCaptainOutcome(state),
        transferOutcome: deriveTransferOutcome(state),
        stakes: state.stakes
      },
      resolveScoringConfig(state.scoringConfig)
    );

    return { score, winnersTarget };
  }, [state]);

  // Live race positions for the post-GW1 sticky bar: woken by league
  // selection (step 3a). The lateStart path has no real standings and
  // falls back to liveReadiness in the controller.
  const liveRace = useMemo(() => {
    if (!state.selectedLeague) return null;

    const config = resolveRaceConfig(state.raceConfig);
    const { state: raceState, gap } = deriveRaceMetrics(
      state.selectedLeague,
      config
    );
    const { userPercent, pacePercent } = mapRaceBarPositions(
      {
        state: raceState,
        gap,
        projectedGap: projectGap(raceState, gap, config)
      },
      config
    );

    return { userPercent, pacePercent };
  }, [state.selectedLeague, state.raceConfig]);

  const contextValue: ReadinessContextValue = useMemo(
    () => ({
      state,
      actions,
      nextGameweek: nextGameweek ?? 1,
      isPreGw1,
      isPostGw1,
      liveReadiness,
      liveRace
    }),
    [state, actions, nextGameweek, isPreGw1, isPostGw1, liveReadiness, liveRace]
  );

  return (
    <ReadinessContext.Provider value={contextValue}>
      {children}
    </ReadinessContext.Provider>
  );
};

export default ReadinessProvider;
